/**
 * CustomDomainCard — Connect a customer-owned domain to the workspace's
 * public site (`/s/<slug>`).
 *
 * MVP flow:
 *   1. Owner enters domain → we store it + generate a verification token
 *   2. We show two DNS records they need to add at their registrar
 *   3. They click Verify → backend resolves the TXT record + flips a flag
 *   4. Once verified, visitors hitting their domain land on their site
 *
 * SSL provisioning is handled separately (ops step) — once verified,
 * Emergent/ops will issue a cert. We display a clear "next step" badge so
 * the user knows what to expect.
 */
import { useEffect, useState } from 'react';
import { Globe, Copy, Check, Loader2, AlertCircle, ShieldCheck, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Button variant="ghost" size="sm" onClick={onCopy} className="h-7 px-2 text-xs flex-shrink-0">
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
}

function DnsRow({ type, host, value }) {
  return (
    <div className="rounded-lg border bg-zinc-50 dark:bg-zinc-900 px-3 py-2.5">
      <div className="grid grid-cols-12 gap-2 items-center text-[11px] uppercase font-semibold text-muted-foreground tracking-wide">
        <span className="col-span-2">Type</span>
        <span className="col-span-4">Host / Name</span>
        <span className="col-span-6">Value</span>
      </div>
      <div className="grid grid-cols-12 gap-2 items-center mt-1.5">
        <span className="col-span-2 font-mono text-xs font-semibold">{type}</span>
        <span className="col-span-4 font-mono text-xs break-all">{host}</span>
        <div className="col-span-6 flex items-center gap-1">
          <span className="font-mono text-xs break-all flex-1">{value}</span>
          <CopyButton value={value} />
        </div>
      </div>
    </div>
  );
}

export default function CustomDomainCard({ workspaceId }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const load = async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/sites/me/domain?workspace_id=${workspaceId}`);
      if (r.ok) {
        const j = await r.json();
        setState(j);
        if (j.domain) setInput(j.domain);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (workspaceId) load(); /* eslint-disable-next-line */ }, [workspaceId]);

  const handleSave = async () => {
    if (!input.trim()) return;
    setSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/sites/me/domain?workspace_id=${workspaceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: input.trim() }),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j.detail || 'Could not save domain');
        return;
      }
      setState(j);
      toast.success('Domain saved — add the DNS records to verify');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const r = await apiFetch(`${API_URL}/api/sites/me/domain/verify?workspace_id=${workspaceId}`, {
        method: 'POST',
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j.detail || 'Verification failed');
        return;
      }
      setState(j);
      toast.success('Domain verified!');
    } finally {
      setVerifying(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Disconnect this custom domain? Your site will go back to the /s/<slug> URL.')) return;
    try {
      await apiFetch(`${API_URL}/api/sites/me/domain?workspace_id=${workspaceId}`, { method: 'DELETE' });
      setState({ domain: null, verified: false });
      setInput('');
      toast.success('Domain disconnected');
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border bg-card p-6 flex items-center justify-center min-h-[120px]">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasDomain = !!state?.domain;
  const verified = !!state?.verified;

  return (
    <div className="rounded-2xl border bg-card p-6" data-testid="custom-domain-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
          <Globe className="w-5 h-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">Custom domain</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Point your own domain (e.g. <span className="font-mono">yourcpa.com</span>) at your CountBucks site.
          </p>
        </div>
        {verified && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
            <ShieldCheck className="w-3.5 h-3.5" />
            Verified
          </span>
        )}
      </div>

      {!hasDomain && (
        <div className="space-y-3">
          <div>
            <Label htmlFor="custom-domain-input">Your domain</Label>
            <div className="mt-1.5 flex gap-2">
              <Input
                id="custom-domain-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="yourcpa.com"
                className="font-mono"
                data-testid="custom-domain-input"
              />
              <Button
                onClick={handleSave}
                disabled={saving || !input.trim()}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="custom-domain-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Use the bare domain (no <span className="font-mono">https://</span>, no <span className="font-mono">www.</span>). You can buy a domain at Namecheap, GoDaddy, or Cloudflare for ~$10/year.
            </p>
          </div>
        </div>
      )}

      {hasDomain && !verified && (
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40 p-3 flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900 dark:text-amber-200">Add these DNS records at your domain registrar</p>
              <p className="text-amber-800/80 dark:text-amber-300/80 text-xs mt-0.5">
                DNS usually propagates within 5-30 minutes. Once it does, click <span className="font-semibold">Verify</span> below.
              </p>
            </div>
          </div>

          <div className="space-y-2.5">
            <DnsRow type="CNAME" host="@" value={state.cname_target} />
            <DnsRow type="TXT" host={state.txt_record_name} value={state.verification_token} />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button
              onClick={handleVerify}
              disabled={verifying}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="custom-domain-verify"
            >
              {verifying ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Checking DNS…</> : 'Verify domain'}
            </Button>
            <Button variant="ghost" onClick={handleRemove} className="text-red-600" data-testid="custom-domain-remove">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            <strong>Note on HTTPS:</strong> after verification, we'll provision an SSL certificate for your domain
            within 24 hours. You'll get an email once your site is fully live at <span className="font-mono">https://{state.domain}</span>.
          </p>
        </div>
      )}

      {hasDomain && verified && (
        <div className="space-y-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900/40 p-3">
            <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
              Your site is live at <a href={`https://${state.domain}`} target="_blank" rel="noopener noreferrer" className="underline font-mono">{state.domain}</a>
            </p>
            <p className="text-xs text-emerald-800/80 dark:text-emerald-300/80 mt-0.5">
              Verified {state.verified_at ? new Date(state.verified_at).toLocaleDateString() : 'just now'}. The "Powered by CountBucks" footer is automatically hidden.
            </p>
          </div>
          <Button variant="outline" onClick={handleRemove} className="text-red-600 border-red-200" data-testid="custom-domain-disconnect">
            Disconnect domain
          </Button>
        </div>
      )}
    </div>
  );
}
